import { useState } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Loader2, Activity, Info } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { getLmsErrorInfo, inferLmsErrorCode } from '@/utils/lmsErrorMessages';

interface LmsConnectionStatusProps {
  /** LMS-type: 'moodle' | 'tutorlms' | 'learndash' | 'xtramile' */
  lmsType: 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';
  /** ID til integrasjonen som skal testes */
  integrationId: string;
  /** memberId — hvis satt brukes member-versjonen av test-endpoint (tilgjengelig for medlem-admin) */
  memberId?: string | null;
  /** Visningsnavn for LMS-et (eks. "Moodle", "TutorLMS") */
  lmsDisplayName: string;
  /** Siste sync-status fra DB ('success' | 'error' | 'running' | 'pending') */
  lastSyncStatus?: string | null;
  /** Tidspunkt for siste sync */
  lastSyncAt?: string | null;
  /** Feilmelding fra siste sync */
  lastSyncError?: string | null;
  /** Connection tested timestamp */
  connectionTestedAt?: string | null;
  /** Connection test error */
  connectionTestError?: string | null;
  /** Callback når test-tilkobling fullføres (gir mulighet til å laste integration-info på nytt) */
  onTestComplete?: (success: boolean) => void;
}

/**
 * Felles statuskort for LMS-integrasjoner som viser:
 * - Siste sync-status med oversatt feilmelding hvis det er feil
 * - "Test tilkobling"-knapp som validerer LMS-tokenet umiddelbart
 * - Detaljert veiledning når noe feiler
 *
 * Brukes i MoodleSettings, TutorLMSSettings, LearnDashSettings, XtramileSettings.
 */
export default function LmsConnectionStatus({
  lmsType,
  integrationId,
  memberId,
  lmsDisplayName,
  lastSyncStatus,
  lastSyncAt,
  lastSyncError,
  connectionTestedAt,
  connectionTestError,
  onTestComplete,
}: LmsConnectionStatusProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string; code?: string } | null>(null);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Bruk member-versjon av endpoint for medlem-admin (xtramile har bare :id-versjon, og den krever ikke admin)
      const supportsMemberEndpoint = lmsType === 'moodle' || lmsType === 'tutorlms' || lmsType === 'learndash';
      const url = memberId && supportsMemberEndpoint
        ? `/api/${lmsType}/integrations/member/${memberId}/test`
        : `/api/${lmsType}/integrations/${integrationId}/test`;
      const response = await authenticatedFetch(url, {
        method: 'POST',
      });
      const data = await response.json();
      const success = !!data.success;
      const code = data.code || (data.error ? inferLmsErrorCode(data.error) : null);
      setTestResult({ success, error: data.error, code: code || undefined });
      onTestComplete?.(success);
    } catch (e: any) {
      const msg = e?.message || 'Nettverksfeil';
      setTestResult({ success: false, error: msg, code: inferLmsErrorCode(msg) || 'network_error' });
      onTestComplete?.(false);
    } finally {
      setTesting(false);
    }
  };

  // Test-feil er kritisk (rødt). Sync-feil OG fersk test-suksess = advarsel (oransje) — token virker, men ett eller flere kurs feiler.
  const testFailed = testResult && !testResult.success;
  const testSucceeded = testResult && testResult.success;
  const hasSyncError = lastSyncStatus === 'error' && lastSyncError;
  const hasConnectionTestError = !!connectionTestError;

  // Prioritet for visning av feildetaljer
  const activeError = testFailed
    ? { message: testResult.error || 'Ukjent feil', code: testResult.code, severity: 'error' as const }
    : hasSyncError
      ? { message: lastSyncError, code: inferLmsErrorCode(lastSyncError) || undefined, severity: testSucceeded ? 'warning' as const : 'error' as const }
      : hasConnectionTestError
        ? { message: connectionTestError, code: inferLmsErrorCode(connectionTestError) || undefined, severity: 'error' as const }
        : null;

  // "Healthy" = ingen kritiske feil. Sync-feil etter vellykket test = ikke kritisk (token virker).
  const isHealthy = !testFailed && (testSucceeded || (!hasSyncError && !hasConnectionTestError && (lastSyncStatus === 'success' || lastSyncStatus === 'pending' || !lastSyncStatus)));
  const isWarning = isHealthy && hasSyncError;

  const errorInfo = activeError ? getLmsErrorInfo(activeError.code, activeError.message) : null;
  const containerColor = !isHealthy
    ? 'bg-red-50 border-red-200'
    : isWarning
      ? 'bg-amber-50 border-amber-200'
      : 'bg-green-50 border-green-200';
  const detailColors = activeError?.severity === 'warning'
    ? { iconBg: 'text-amber-600', titleText: 'text-amber-900', descText: 'text-amber-800', borderTop: 'border-amber-200', detailBorder: 'border-amber-200' }
    : { iconBg: 'text-red-600', titleText: 'text-red-900', descText: 'text-red-800', borderTop: 'border-red-200', detailBorder: 'border-red-200' };

  const formatRelative = (iso?: string | null) => {
    if (!iso) return 'aldri';
    const ms = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'akkurat nå';
    if (minutes < 60) return `${minutes} min siden`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} t siden`;
    const days = Math.floor(hours / 24);
    return `${days} d siden`;
  };

  return (
    <div className={`rounded-lg border p-4 ${containerColor}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {!isHealthy ? (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          ) : isWarning ? (
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className={`font-medium ${!isHealthy ? 'text-red-900' : isWarning ? 'text-amber-900' : 'text-green-900'}`}>
              {testSucceeded && isWarning
                ? `${lmsDisplayName} svarer, men sync av ett eller flere kurs feilet`
                : testSucceeded
                  ? `Tilkoblingstest vellykket — ${lmsDisplayName} svarer normalt`
                  : testFailed
                    ? `Tilkoblingstest feilet`
                    : isWarning
                      ? `${lmsDisplayName}-integrasjonen virker, men sync feilet for ett eller flere kurs`
                      : isHealthy
                        ? `${lmsDisplayName}-integrasjonen er aktiv`
                        : `${lmsDisplayName}-integrasjonen har problemer`}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              <Activity className="inline w-3 h-3 mr-1" />
              Siste sync: <strong>{lastSyncStatus || 'aldri'}</strong> · {formatRelative(lastSyncAt)}
              {connectionTestedAt && (
                <span> · Siste tilkoblingstest: {formatRelative(connectionTestedAt)}</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={runTest}
          disabled={testing}
          className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-md text-sm font-medium text-gray-700 flex items-center gap-2 disabled:opacity-50 flex-shrink-0"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
          {testing ? 'Tester...' : 'Test tilkobling'}
        </button>
      </div>

      {/* Utvidet feilinformasjon */}
      {errorInfo && (
        <div className={`mt-3 pt-3 border-t ${detailColors.borderTop}`}>
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle size={16} className={`${detailColors.iconBg} flex-shrink-0 mt-0.5`} />
            <div>
              <p className={`font-semibold ${detailColors.titleText} text-sm`}>{errorInfo.title}</p>
              <p className={`text-sm ${detailColors.descText} mt-1`}>{errorInfo.description}</p>
            </div>
          </div>
          <div className={`ml-6 mt-2 bg-white border ${detailColors.detailBorder} rounded p-3`}>
            <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Info size={12} />
              Hva du kan gjøre:
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-line">{errorInfo.guidance}</p>
            {errorInfo.code !== 'unknown' && (
              <p className="text-[11px] text-gray-500 mt-2 font-mono">Feilkode: {errorInfo.code}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
